import { useMemo, useState } from 'react';
import {
  Table,
  Badge,
  ActionIcon,
  Group,
  Text,
  Menu,
  rem,
  TextInput,
  Paper,
  Stack,
  Image,
  Avatar,
  Checkbox,
  Button,
  Box,
  Center,
  Loader,
} from '@mantine/core';
import {
  IconDotsVertical,
  IconEdit,
  IconTrash,
  IconEye,
  IconSearch,
  IconUserPlus,
  IconUserMinus,
  IconPhoto,
  IconFilter,
} from '@tabler/icons-react';
import { formatDate, formatCurrency } from '@/lib/utils';
import { useColumnVisibility, type ColumnDefinition } from '@/hooks/useColumnVisibility';
import { useTableSort } from '@/hooks/useTableSort';
import { ColumnVisibilityMenu } from '@/components/ui/ColumnVisibilityMenu';
import { SortableTableHeader } from '@/components/ui/SortableTableHeader';
import { AssetMobileCard } from './AssetMobileCard';
import type { Asset } from '@/types/models';

interface AssetTableProps {
  assets: Asset[];
  onView: (asset: Asset) => void;
  onEdit: (asset: Asset) => void;
  onDelete: (asset: Asset) => void;
  onAssign?: (asset: Asset) => void;
  onUnassign?: (asset: Asset) => void;
  isLoading?: boolean;
  selectedIds?: string[];
  onSelectionChange?: (ids: string[]) => void;
  onOpenFilters?: () => void;
  activeFilterCount?: number;
}

const statusColors: Record<string, string> = {
  Available: 'green',
  Assigned: 'blue',
  Maintenance: 'yellow',
  Retired: 'gray',
  Lost: 'red',
};

type AssetColumnKey =
  | 'photo'
  | 'tag_id'
  | 'description'
  | 'serial_number'
  | 'status'
  | 'category'
  | 'location'
  | 'assigned_to'
  | 'cost'
  | 'purchase_date';

const ASSET_COLUMNS: ColumnDefinition<AssetColumnKey>[] = [
  { key: 'photo', label: 'Photo', sortable: false, defaultVisible: true },
  { key: 'tag_id', label: 'Tag ID', sortable: true, defaultVisible: true },
  { key: 'description', label: 'Description', sortable: true, defaultVisible: true },
  { key: 'serial_number', label: 'Serial Number', sortable: true, defaultVisible: true },
  { key: 'status', label: 'Status', sortable: true, defaultVisible: true },
  { key: 'category', label: 'Category', sortable: true, defaultVisible: true },
  { key: 'location', label: 'Location', sortable: true, defaultVisible: true },
  { key: 'assigned_to', label: 'Assigned To', sortable: false, defaultVisible: true },
  { key: 'cost', label: 'Cost', sortable: true, defaultVisible: false },
  { key: 'purchase_date', label: 'Purchase Date', sortable: true, defaultVisible: false },
];

export function AssetTable({
  assets,
  onView,
  onEdit,
  onDelete,
  onAssign,
  onUnassign,
  isLoading,
  selectedIds = [],
  onSelectionChange,
  onOpenFilters,
  activeFilterCount = 0,
}: AssetTableProps) {
  const [search, setSearch] = useState('');

  // Column visibility
  const columnVisibility = useColumnVisibility({
    cookieName: 'asset-table-columns',
    columns: ASSET_COLUMNS,
  });

  const toggleRow = (id: string) => {
    if (!onSelectionChange) return;
    if (selectedIds.includes(id)) {
      onSelectionChange(selectedIds.filter((selectedId) => selectedId !== id));
    } else {
      onSelectionChange([...selectedIds, id]);
    }
  };

  const toggleAll = () => {
    if (!onSelectionChange) return;
    if (selectedIds.length === filteredAssets.length) {
      onSelectionChange([]);
    } else {
      onSelectionChange(filteredAssets.map((asset) => asset.id));
    }
  };

  const filteredAssets = useMemo(() => {
    return assets.filter((asset) => {
      const matchesSearch =
        search === '' ||
        asset.description?.toLowerCase().includes(search.toLowerCase()) ||
        asset.tag_id?.toLowerCase().includes(search.toLowerCase()) ||
        asset.brand?.toLowerCase().includes(search.toLowerCase()) ||
        asset.model?.toLowerCase().includes(search.toLowerCase()) ||
        asset.serial_number?.toLowerCase().includes(search.toLowerCase());

      return matchesSearch;
    });
  }, [assets, search]);

  // Sorting
  const { sortedData, sortKey, sortDirection, handleSort } = useTableSort({
    data: filteredAssets,
  });

  // Calculate colspan for empty states
  const visibleColumnCount = ASSET_COLUMNS.filter((col) =>
    columnVisibility.isColumnVisible(col.key)
  ).length;
  const totalColumns = visibleColumnCount + (onSelectionChange ? 1 : 0) + 1; // +1 for actions

  const rows = sortedData.map((asset) => (
    <Table.Tr key={asset.id}>
      {onSelectionChange && (
        <Table.Td>
          <Checkbox
            checked={selectedIds.includes(asset.id)}
            onChange={() => toggleRow(asset.id)}
          />
        </Table.Td>
      )}
      {columnVisibility.isColumnVisible('photo') && (
        <Table.Td>
          {asset.photo_url ? (
            <Image
              src={asset.photo_url}
              alt={asset.description}
              w={50}
              h={50}
              radius="sm"
              fit="cover"
            />
          ) : (
            <Avatar color="gray" size={50} radius="sm">
              <IconPhoto size={24} />
            </Avatar>
          )}
        </Table.Td>
      )}
      {columnVisibility.isColumnVisible('tag_id') && (
        <Table.Td>
          <Text fw={500}>{asset.tag_id}</Text>
        </Table.Td>
      )}
      {columnVisibility.isColumnVisible('description') && (
        <Table.Td>
          <div>
            <Text size="sm" fw={500}>
              {asset.description}
            </Text>
            <Text size="xs" c="dimmed">
              {asset.brand} {asset.model}
            </Text>
          </div>
        </Table.Td>
      )}
      {columnVisibility.isColumnVisible('serial_number') && (
        <Table.Td>{asset.serial_number}</Table.Td>
      )}
      {columnVisibility.isColumnVisible('status') && (
        <Table.Td>
          <Badge color={statusColors[asset.status]} variant="light">
            {asset.status}
          </Badge>
        </Table.Td>
      )}
      {columnVisibility.isColumnVisible('category') && (
        <Table.Td>{asset.category?.name || '-'}</Table.Td>
      )}
      {columnVisibility.isColumnVisible('location') && (
        <Table.Td>{asset.location?.name || '-'}</Table.Td>
      )}
      {columnVisibility.isColumnVisible('assigned_to') && (
        <Table.Td>
          {asset.assigned_to && asset.assigned_to.length > 0 ? (
            <div>
              {asset.assigned_to.map((person, idx) => (
                <Text key={person.id} size="sm">
                  {person.full_name}
                  {idx < asset.assigned_to!.length - 1 && ', '}
                </Text>
              ))}
            </div>
          ) : (
            <Text size="sm" c="dimmed">-</Text>
          )}
        </Table.Td>
      )}
      {columnVisibility.isColumnVisible('cost') && (
        <Table.Td>{formatCurrency(asset.cost)}</Table.Td>
      )}
      {columnVisibility.isColumnVisible('purchase_date') && (
        <Table.Td>{formatDate(asset.purchase_date)}</Table.Td>
      )}
      <Table.Td>
        <Group gap="xs" justify="flex-end">
          <ActionIcon
            variant="subtle"
            color="blue"
            onClick={() => onView(asset)}
            title="View Details"
          >
            <IconEye size={16} />
          </ActionIcon>
          <ActionIcon
            variant="subtle"
            color="gray"
            onClick={() => onEdit(asset)}
            title="Edit"
          >
            <IconEdit size={16} />
          </ActionIcon>
          <Menu shadow="md" width={200} position="bottom-end">
            <Menu.Target>
              <ActionIcon variant="subtle" color="gray">
                <IconDotsVertical size={16} />
              </ActionIcon>
            </Menu.Target>

            <Menu.Dropdown>
              {onAssign && asset.status !== 'Assigned' && (
                <Menu.Item
                  leftSection={<IconUserPlus style={{ width: rem(14), height: rem(14) }} />}
                  onClick={() => onAssign(asset)}
                >
                  Assign to Person
                </Menu.Item>
              )}
              {onUnassign && asset.status === 'Assigned' && (
                <Menu.Item
                  leftSection={<IconUserMinus style={{ width: rem(14), height: rem(14) }} />}
                  onClick={() => onUnassign(asset)}
                >
                  Unassign
                </Menu.Item>
              )}
              {(onAssign || onUnassign) && <Menu.Divider />}
              <Menu.Item
                color="red"
                leftSection={<IconTrash style={{ width: rem(14), height: rem(14) }} />}
                onClick={() => onDelete(asset)}
              >
                Delete
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </Table.Td>
    </Table.Tr>
  ));

  // Mobile card list - iOS-style: checkbox for selection, main area for viewing details
  const mobileCards = sortedData.map((asset) => (
    <AssetMobileCard
      key={asset.id}
      asset={asset}
      onView={onView}
      onEdit={onEdit}
      onDelete={onDelete}
      onAssign={onAssign}
      onUnassign={onUnassign}
      isSelected={selectedIds.includes(asset.id)}
      onSelectionChange={
        onSelectionChange
          ? (selected) => {
              if (selected) {
                onSelectionChange([...selectedIds, asset.id]);
              } else {
                onSelectionChange(selectedIds.filter((id) => id !== asset.id));
              }
            }
          : undefined
      }
    />
  ));

  return (
    <Stack gap="md">
      {/* Search and filters */}
      <Paper p="md" withBorder>
        <Group gap="sm" wrap="wrap">
          <TextInput
            placeholder="Search assets..."
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            style={{ flex: 1, minWidth: 200 }}
          />

          {onOpenFilters && (
            <Button
              variant="light"
              leftSection={<IconFilter size={16} />}
              onClick={onOpenFilters}
              rightSection={
                activeFilterCount > 0 ? (
                  <Badge size="sm" variant="filled" color="blue" circle>
                    {activeFilterCount}
                  </Badge>
                ) : undefined
              }
            >
              <Box visibleFrom="xs">Advanced Filters</Box>
              <Box hiddenFrom="xs">Filter</Box>
            </Button>
          )}
          {/* Hide column menu on mobile since we show cards */}
          <Box visibleFrom="sm">
            <ColumnVisibilityMenu
              columns={ASSET_COLUMNS}
              visibleColumns={columnVisibility.visibleColumns}
              onToggleColumn={columnVisibility.toggleColumn}
              onShowAll={columnVisibility.showAllColumns}
              onResetDefaults={columnVisibility.resetToDefaults}
            />
          </Box>
        </Group>
      </Paper>

      {/* Mobile card view - visible on small screens */}
      <Box hiddenFrom="sm">
        {isLoading ? (
          <Center py="xl">
            <Loader />
          </Center>
        ) : sortedData.length === 0 ? (
          <Paper withBorder p="xl">
            <Text ta="center" c="dimmed">
              No assets found
            </Text>
          </Paper>
        ) : (
          <Stack gap="sm">{mobileCards}</Stack>
        )}
      </Box>

      {/* Desktop table view - visible on larger screens */}
      <Box visibleFrom="sm">
        <Paper withBorder>
          <Table.ScrollContainer minWidth={800}>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  {onSelectionChange && (
                    <Table.Th>
                      <Checkbox
                        checked={selectedIds.length === filteredAssets.length && filteredAssets.length > 0}
                        indeterminate={selectedIds.length > 0 && selectedIds.length < filteredAssets.length}
                        onChange={toggleAll}
                      />
                    </Table.Th>
                  )}
                  {columnVisibility.isColumnVisible('photo') && <Table.Th>Photo</Table.Th>}
                  {columnVisibility.isColumnVisible('tag_id') && (
                    <SortableTableHeader
                      label="Tag ID"
                      sortKey="tag_id"
                      currentSortKey={sortKey}
                      currentDirection={sortDirection}
                      onSort={handleSort}
                    />
                  )}
                  {columnVisibility.isColumnVisible('description') && (
                    <SortableTableHeader
                      label="Description"
                      sortKey="description"
                      currentSortKey={sortKey}
                      currentDirection={sortDirection}
                      onSort={handleSort}
                    />
                  )}
                  {columnVisibility.isColumnVisible('serial_number') && (
                    <SortableTableHeader
                      label="Serial Number"
                      sortKey="serial_number"
                      currentSortKey={sortKey}
                      currentDirection={sortDirection}
                      onSort={handleSort}
                    />
                  )}
                  {columnVisibility.isColumnVisible('status') && (
                    <SortableTableHeader
                      label="Status"
                      sortKey="status"
                      currentSortKey={sortKey}
                      currentDirection={sortDirection}
                      onSort={handleSort}
                    />
                  )}
                  {columnVisibility.isColumnVisible('category') && (
                    <SortableTableHeader
                      label="Category"
                      sortKey="category.name"
                      currentSortKey={sortKey}
                      currentDirection={sortDirection}
                      onSort={handleSort}
                    />
                  )}
                  {columnVisibility.isColumnVisible('location') && (
                    <SortableTableHeader
                      label="Location"
                      sortKey="location.name"
                      currentSortKey={sortKey}
                      currentDirection={sortDirection}
                      onSort={handleSort}
                    />
                  )}
                  {columnVisibility.isColumnVisible('assigned_to') && <Table.Th>Assigned To</Table.Th>}
                  {columnVisibility.isColumnVisible('cost') && (
                    <SortableTableHeader
                      label="Cost"
                      sortKey="cost"
                      currentSortKey={sortKey}
                      currentDirection={sortDirection}
                      onSort={handleSort}
                    />
                  )}
                  {columnVisibility.isColumnVisible('purchase_date') && (
                    <SortableTableHeader
                      label="Purchase Date"
                      sortKey="purchase_date"
                      currentSortKey={sortKey}
                      currentDirection={sortDirection}
                      onSort={handleSort}
                    />
                  )}
                  <Table.Th>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {isLoading ? (
                  <Table.Tr>
                    <Table.Td colSpan={totalColumns}>
                      <Text ta="center" py="xl" c="dimmed">
                        Loading assets...
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ) : rows.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={totalColumns}>
                      <Text ta="center" py="xl" c="dimmed">
                        No assets found
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  rows
                )}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Paper>
      </Box>

      <Text size="sm" c="dimmed">
        Showing {filteredAssets.length} of {assets.length} assets
      </Text>
    </Stack>
  );
}
